import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { catchError, concat, debounceTime, filter, first, map, of, switchMap, tap } from 'rxjs';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { documentDialog, ToastService } from '@senet-uiverse/ui';
import { IStorageData } from '@senet-id/core/types';
import { LanguageCode } from '@senet-uiverse/i18n';
import {
  IDocumentSignatureStatus,
  ISignatureStatusParams,
  DocumentsApiService,
  SECOND_MILLISECONDS,
  EDocumentOwnerType,
  IDocumentForSign,
  IDocumentVersion,
  EDocumentSource,
  EDocumentStatus,
  EDocumentUser,
} from '@senet-uiverse/common';

import { ProfileService } from './profile.service';
import { StorageService } from './storage.service';

const keys = [
  marker('enums.document.type.terms-and-conditions'),
  marker('enums.document.type.venue-rules'),
  marker('enums.document.type.eula'),
];
const STAFF_ACCOUNT_TYPE = 3;

type TParams = Required<Pick<ISignatureStatusParams, 'organizationId' | 'keycloakUserId' | 'userGroup'>>;

@Injectable({
  providedIn: 'root',
})
export class DocumentsService extends DocumentsApiService {
  private readonly appStorage = inject(StorageService).storage;
  private readonly profileService = inject(ProfileService);
  private readonly toastService = inject(ToastService);

  readonly params = computed<TParams | null>(() => this.getParams(this.appStorage()));
  readonly userGroup = computed(() => this.params()?.userGroup || null);
  readonly organizationDocuments = signal<IDocumentVersion[] | null>(null);
  readonly params$ = toObservable(this.params).pipe(filter(Boolean));
  readonly documentDialog = documentDialog();
  endpointsConfig = { urlFn: (endpoint: string) => endpoint.replace('/api/v1', '') };
  queryKey = undefined;

  get orgLanguage() {
    return this.appStorage().organizationInfo?.lang || LanguageCode.en;
  }

  subscribeToOrganizationSignaturesStatus() {
    return this.params$.pipe(
      debounceTime(SECOND_MILLISECONDS),
      switchMap((params) => this.getOrganizationSignaturesStatus(params!)),
      catchError(() => of([])),
      switchMap((result) => {
        if (result.length === 0) {
          return of(null);
        }

        const { keycloakUserId, organizationId } = this.params() || {};

        if (!keycloakUserId || !organizationId) {
          console.warn(
            'Documents: There is no Keycloak user ID or organization id, so it is not possible to sign documents.',
          );

          return of(null);
        }

        const dialogs$ = result.map((document) => {
          return this.documentDialog
            .openWithConfirmation({
              confirmButton: this.translateService.instant(marker('common.buttons.confirm')),
              documentLink: document.translation.file,
              title: document.documentName,
            })
            .pipe(
              switchMap(() => {
                return this.signDocument({
                  language: document.translation.language,
                  documentId: document.document.id,
                  channel: EDocumentSource.SenetId,
                  keycloakUserId,
                });
              }),
              tap(() => {
                // Після успішного підписання оновлюємо статус документа в кеші, щоб не показувати діалог повторно для того ж документа
                this.updateQueryDocumentSignStatus(document.document.id, organizationId, keycloakUserId);
                this.toastService.success({
                  message: this.translateService.instant(marker('notifications.sign-document.success')),
                });
              }),
              catchError((error: HttpErrorResponse) => {
                // Якщо помилка BadRequest, то оновлюємо статус документа в кеші, щоб не показувати діалог повторно для того ж документа,
                // бо це означає, що документ уже підписаний.
                // В інших випадках показуємо повідомлення про помилку.
                if (error.status === HttpStatusCode.BadRequest) {
                  this.updateQueryDocumentSignStatus(document.document.id, organizationId, keycloakUserId);
                } else {
                  this.toastService.danger({
                    message: this.translateService.instant(marker('notifications.sign-document.error')),
                  });
                }

                return of(null);
              }),
            );
        });

        return concat(...dialogs$);
      }),
    );
  }

  // Отримуємо документи, котрі потребують підпису та записуємо документи організації в сигнал для відображення в контактах
  getOrganizationSignaturesStatus(params: TParams) {
    this.organizationDocuments.set(null);

    return this.useQuery({
      queryKey: this.getSignaturesQueryKey(params.organizationId, params.keycloakUserId),
      queryFn: () =>
        this.getSignaturesStatus({ ...params, channel: EDocumentSource.SenetId }).pipe(map((result) => result.data)),
    }).result$.pipe(
      filter((query) => query.status !== 'pending'),
      map((result) => result.data || []),
      tap((data) => {
        const orgDocs = data
          .filter((item) => item.document.ownerType === EDocumentOwnerType.Venue)
          .map((item) => item.document);

        this.organizationDocuments.set(orgDocs);
      }),
      map((data) => {
        return data
          .filter((item) => item.requireSignature)
          .map((item) =>
            this.mapDocumentForSign(item.document, this.translateService.currentLang as LanguageCode, this.orgLanguage),
          );
      }),
      first(),
    );
  }

  getDocumentsSignatures({ page, limit, organizationId }: { page: number; limit: number; organizationId: string }) {
    return this.getSignatures({
      keycloakUserId: this.params()?.keycloakUserId,
      offset: page === 1 ? 0 : (page - 1) * limit,
      organizationId,
      limit: limit,
    });
  }

  // Отримуємо SENET документи для відображення у футері та формі реєстрації
  getSenetDocuments() {
    return this.useQuery({
      queryKey: ['documents', 'senet'],
      queryFn: () => {
        return this.getPublicLatest({
          channel: EDocumentSource.SenetId,
          status: EDocumentStatus.Published,
        }).pipe(map((result) => result.data));
      },
    }).result$.pipe(
      filter((query) => query.status !== 'pending'),
      map((result) => result.data || []),
      first(),
    );
  }

  getOrganizationRegistrationDocuments(organizationId: string, organizationLang: LanguageCode) {
    return this.getLatest({
      status: EDocumentStatus.Published,
      userGroup: EDocumentUser.Gamer,
      organizationId: organizationId,
    }).pipe(
      map((result) => result.data || []),
      map((documents) =>
        documents.map((document) =>
          this.mapDocumentForSign(document, this.translateService.currentLang as LanguageCode, organizationLang),
        ),
      ),
    );
  }

  openDocument(document: IDocumentForSign) {
    return this.documentDialog.open({
      documentLink: document.translation.file,
      title: document.documentName,
    });
  }

  private getParams(storageData: IStorageData) {
    const { organizationInfo, organizationProfile, user } = storageData;
    const keycloakUserId = this.profileService.getUserKeycloakId(user);
    const organizationId = organizationInfo?.senet_server_uid;

    if (!organizationId || !organizationProfile || !keycloakUserId) {
      return null;
    }

    return {
      userGroup:
        organizationProfile.active_account.account_type === STAFF_ACCOUNT_TYPE
          ? EDocumentUser.Staff
          : EDocumentUser.Gamer,
      organizationId,
      keycloakUserId,
    };
  }

  private getSignaturesQueryKey(organizationId: string, keycloakUserId: string) {
    return ['documents', 'signatures-status', organizationId, keycloakUserId];
  }

  // Змінює статус підпису документа в кеші після підписання, щоб не показувати діалог повторно для того ж документа
  private updateQueryDocumentSignStatus(documentId: string, organizationId: string, keycloakUserId: string) {
    this.queryClient.setQueryData(
      this.getSignaturesQueryKey(organizationId, keycloakUserId),
      (state: IDocumentSignatureStatus[]) => {
        const list = Array.isArray(state) ? [...state] : [];
        const idx = list?.findIndex((el: IDocumentSignatureStatus) => el.document.id === documentId);

        if (idx > -1) {
          list[idx] = { ...list[idx], requireSignature: false };
        }

        return list;
      },
    );
  }
}
